<template>
    <div class="alarm-container">
        <div
            class="alarm-log-item pb-1 pr-3"
            v-for="alarm in alarms.rows"
            :key="alarm.alarmIdx"
            :value="alarm"
            @click="openModal(alarm.alarmIdx)"
            :class="{ 'alarm-log-item--active': alarm.alarmIdx === activeAlarmIdx }"
        >
            <div class="alarm-list-body">
                <div class="alarm-item">
                    <div class="alarm-bell">
                        <div class="alarm-bell-icon">
                            <i class="material-icons" :style="{ color: calSeverity(alarm.severity) }">
                                notifications_none
                            </i>
                            <span
                                class="alarm-badge"
                                v-if="alarm.eventCount > 0"
                                :style="getBadgeStyle(alarm.eventCount, alarm.severity)"
                            >
                                {{ Number(alarm.eventCount) > 999 ? "999+" : alarm.eventCount }}
                            </span>
                        </div>
                        <div :style="{ color: calSeverity(alarm.severity) }" class="alarm-severity-font">
                            {{ transAlarmSeverity(alarm.severity) }}
                        </div>
                    </div>
                    <div class="alarm-body">
                        <div class="alarm-body-header">
                            <div class="alarm-body-title">
                                {{ convertAlarmType(alarm.alarmType) }}
                            </div>
                            <div class="alarm-date">
                                {{ new Date(alarm.modDate).passedTime() }}
                            </div>
                        </div>

                        <div class="alarm-body-text">
                            <div class="alarm-message">{{ alarm.message }}</div>
                        </div>
                    </div>
                </div>
                <div class="dismiss-alarm inline-action-toggle">
                    <i @click.stop="toggleDismissMenu(alarm.alarmIdx)" class="fas fa-ellipsis-v alarm-stop-icon"></i>
                </div>
            </div>
            <transition name="fade-popup">
                <div v-if="activeDismissMenu === alarm.alarmIdx" class="alarm-inline-actions">
                    <button
                        class="btn bg-white btn-sm mr-2 handleAlarmStateButton"
                        @click.stop="handleAlarmDismiss(alarm.alarmIdx, 'Acked')"
                    >
                        인지
                    </button>
                    <button
                        class="btn bg-white btn-sm mr-2 handleAlarmStateButton"
                        @click.stop="handleAlarmDismiss(alarm.alarmIdx, 'Resolved')"
                    >
                        해결
                    </button>
                    <button
                        class="btn btn-danger btn-sm handleAlarmStateButton"
                        @click.stop="handleAlarmDismiss(alarm.alarmIdx, 'Dismissed')"
                    >
                        무시
                    </button>
                </div>
            </transition>
        </div>
        <transition name="fade-popup">
            <div class="custom-alarm-modal" v-if="alarmDetail">
                <div class="modal-header">
                    <h5 class="modal-title">알람 상세</h5>
                    <div v-if="selectedAlaramEventArr.length !== 0" class="handle-alarm-state-div ml-auto">
                        <button
                            v-for="(control, index) in controls"
                            :key="index"
                            @click="handleButtonClick(control)"
                            class="ml-2 alarm-event-button-group btn btn-sm"
                            :class="control.class"
                        >
                            {{ control.label }}
                        </button>
                    </div>

                    <button @click.stop="closeCustomModal" class="alarm-event-hide">
                        <i class="fas fa-times alarm-stop-icon"></i>
                    </button>
                </div>

                <div class="modal-body">
                    <!-- 
                    1. @click => 보정 모달 열기 클릭 이벤트
                    2. 알람 상세의 alarm 정보를 사용하기 위해 alarmEvents 를 alarmDetail로 변수명 변경
                    made by woonsik 2025.05.09
                -->
                    <div v-for="(event, index) in alarmDetail.events" :key="index" class="alarm-event">
                        <b-form-checkbox
                            type="checkbox"
                            :id="`alarm-event-${event.eventIdx}`"
                            :checked="selectedAlaramEventArr.includes(event.eventIdx)"
                            @change="toggleAlarmEventSelection(event.alarmLogIdx)"
                            class="alarm-event-checkbox"
                        />

                        <div
                            :for="`alarm-event-${event.alarmLogIdx}`"
                            class="alarm-event-icon-message"
                            @click.stop="showTotalLogModal(event)"
                        >
                            <div class="alarm-bell-icon">
                                <i class="material-icons" :style="{ color: calSeverity(event.severity) }">
                                    notifications_none
                                </i>
                                <div :style="{ color: calSeverity(event.severity) }" class="alarm-severity-font">
                                    {{ transAlarmSeverity(event.severity) }}
                                </div>
                            </div>
                            <div class="alarm-event-text">
                                <div class="alarm-body-header">
                                    <div class="alarm-event-detail-name">
                                        {{ event.eventObjName || findAlarmEventName() }}
                                    </div>
                                    <div class="alarm-date">
                                        {{ new Date(event.regDate).passedTime() }}
                                    </div>
                                </div>

                                <div class="alarm-message">{{ event.message }}</div>
                            </div>
                        </div>
                    </div>
                    <div v-if="alarmDetail.events.length >= 50" class="div-alarmLimit">
                        📢 상세 알람은 최대 50건까지 표시됩니다.
                    </div>
                </div>
                <div class="alarm-detail-bottom"></div>
            </div>
        </transition>
        <TotalLogModal
            v-if="pointInfo"
            ref="TotalLogModal"
            :pointInfo="pointInfo"
            :alarmDate="alarmDate"
            :alarmDetail="alarmDetail"
            :eventInfo="eventInfo"
            :type="'Alarm'"
            :routeMgmt="true"
            @hide="hideTotalLogModal"
            @hide-alarm-popup="handleTotalLogModalClose"
        />
        <EventLogModal v-if="eventLogIdx" ref="EventLogModal" :eventLogIdx="eventLogIdx" :readonly="true" />
    </div>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import TotalLogModal from "@src/views/new/point-management/point-mgmt/modal/TotalLogModal.vue";
import EventLogModal from "@src/views/sys/system-log/eventLog/EventLogPopup.vue";
import { mapGetters, mapActions } from "vuex";

export default {
    props: ["alarms", "alarlDropdownShow"],
    components: {
        TotalLogModal,
        EventLogModal,
    },
    data() {
        return {
            activeAlarmIdx: null,
            controls: [
                {
                    event: "Acked",
                    label: "인지",
                    class: "btn bg-white btn-sm mr-2 handleAlarmEventButton",
                },
                {
                    event: "Resolved",
                    label: "해결",
                    class: "btn bg-white btn-sm mr-2 handleAlarmEventButton",
                },
                {
                    event: "Dismissed",
                    label: "무시",
                    class: "btn btn-danger btn-sm handleAlarmEventButton",
                },
            ],

            highColor: "red",
            midColor: "orange",
            lowColor: "green",

            // alarmEvents: null,
            alarmDetail: null,

            selectedAlaramEventArr: [],
            currentAlarmIdx: null,
            activeDismissMenu: null,
            pointInfo: null,
            alarmDate: null,
            eventInfo: null,
            eventLogIdx: null, // system alarm
        };
    },
    watch: {
        alarlDropdownShow(flag) {
            if (!flag) {
                this.activeDismissMenu = null;
            }
        },
    },
    computed: {
        ...mapGetters(["commonCodes"]),
    },
    methods: {
        ...mapActions({ updateAlarmBadge: "UPDATE_ALARM_BADGE" }),
        getBadgeStyle(count, severity) {
            const digits = count > 999 ? 4 : String(count).length;
            // const digits = 4;
            const positionMap = {
                1: "12px",
                2: "11px",
                3: "10px",
                4: "4px",
            };
            return {
                backgroundColor: this.calSeverity(severity),
                color: "#fff",
                opacity: 0.8,
                right: positionMap[digits] || "-12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: digits === 1 ? "15px" : "",
                height: digits === 1 ? "15px" : "",
                borderRadius: digits === 1 ? "50%" : "",
            };
        },
        handleTotalLogModalClose() {
            this.$emit("close-alarm-dropdown");
        },
        closeCustomModal() {
            this.alarmDetail = null;
            this.selectedAlaramEventArr = [];
            this.activeAlarmIdx = null;
        },

        convertAlarmType(type) {
            const found = this.commonCodes.alarmType.find((i) => i.value === type);
            return found ? found.text : type;
        },
        toggleDismissMenu(alarmIdx) {
            this.activeDismissMenu = this.activeDismissMenu === alarmIdx ? null : alarmIdx;
        },
        async handleAlarmDismiss(idx, status) {
            await this.updateAlarmState(idx, status);
            this.updateAlarmBadge(true);

            this.activeDismissMenu = null;
        },
        async updateAlarmState(idx, state) {
            const payload = { alarmIdx: idx, status: state };
            try {
                const result = await backEndApi.newAlarm.updateAlarmStatus(payload);
                if (result.status === 200) {
                    this.$emit("update-alarm-list");
                }
            } catch (e) {
                console.warn(e);
            }
        },
        async updateAlaramEvent(alarmLogIdx, state) {
            const changeSatus = {
                alarmLogIdx: alarmLogIdx,
                status: state,
            };
            try {
                let result = await backEndApi.newAlarm.updateAlarmLogStatus(changeSatus);
                if (result.status === 200) {
                    this.selectedAlaramEventArr = [];
                    this.updateAlarmBadge(true);
                }
            } catch (e) {
                console.warn("error", e);
            }
        },
        async handleButtonClick(state) {
            if (!this.selectedAlaramEventArr.length) {
                this.$bvToast.toast("알람을 선택해주세요.", { title: "알림", autoHideDelay: 2000, solid: true });
                return;
            }
            const res = await this.alertConfirmWarning(`알람을 ${state.label}하시겠습니까?`);
            if (!res.value) return;
            this.selectedAlaramEventArr.forEach((eventIdx) => {
                this.updateAlaramEvent(eventIdx, state.event);
            });
            const detail = await this.getAlarmDetail(this.currentAlarmIdx);
            // this.alarmEvents = detail.data.events;
            this.alarmDetail = detail.data;
        },
        async getAlarmDetail(id) {
            return backEndApi.newAlarm.getAlarmDetail(id, "Active", 7, 50, 0);
        },
        toggleAlarmEventSelection(id) {
            const idx = this.selectedAlaramEventArr.indexOf(id);
            idx === -1 ? this.selectedAlaramEventArr.push(id) : this.selectedAlaramEventArr.splice(idx, 1);
        },
        findAlarmEventName() {
            return this.alarms.rows.find((a) => a.alarmIdx === this.currentAlarmIdx)?.eventObjName;
        },
        transAlarmSeverity(sev) {
            const found = this.commonCodes.alarmSeverity.find((i) => i.value === sev);
            return found ? found.text : "";
        },
        calSeverity(val) {
            return (
                { Critical: this.highColor, High: this.highColor, Medium: this.midColor, Low: this.lowColor }[val] ||
                "#888"
            );
        },
        async openModal(alarmIdx) {
            this.activeAlarmIdx = alarmIdx;
            this.currentAlarmIdx = alarmIdx;

            this.alarmDetail = null;
            this.$nextTick(async () => {
                const { data } = await this.getAlarmDetail(alarmIdx);
                this.alarmDetail = data;
            });
        },
        onModalClose() {
            this.selectedAlaramEventArr = [];
            this.activeAlarmIdx = null;
        },

        // 보정 모달 열기
        async showTotalLogModal(event) {
            if (this.alarmDetail.alarm.eventSrcType === "Point") {
                const date = event.regDate.split(" ")[0];
                // const findDetail = this.alarmDetail.events.find((item) => item.eventIdx == event.eventIdx);

                this.alarmDate = date ? date : null;
                // this.eventDetail = findDetail ? findDetail : null;
                this.eventInfo = event;

                await this.getPointInfo(this.alarmDetail.alarm.eventObjIdx);
                if (!this.isEmpty(this.pointInfo)) {
                    this.$nextTick(() => {
                        this.$refs.TotalLogModal.showModal();
                    });
                }
            } else if (this.alarmDetail.alarm.eventSrcType === "System") {
                this.eventLogIdx = event.eventIdx;
                if (!this.isEmpty(this.eventLogIdx)) {
                    this.$nextTick(() => {
                        this.$refs.EventLogModal.show();
                    });
                }
            } else return;
        },
        // 보정 모달 닫기
        async hideTotalLogModal() {
            this.pointInfo = null;
            this.alarmDate = null;
        },

        // 관제점 상세 정보 가져오기
        async getPointInfo(idx) {
            try {
                const result = await backEndApi.pointInfo.getPointDetailNew(idx);
                if (result.status === 200) {
                    this.pointInfo = result.data;
                } else {
                    this.alertWarning(`${result.data.message}`);
                }
            } catch (e) {
                console.warn(e);
            }
        },
    },
};
</script>
<style scoped>
.alarm-body-header {
    display: flex;
    width: 100%;
    justify-content: space-between;
}
.alarm-body-title {
    font-size: 0.7rem;
    font-weight: bold;
    color: black;
}

.alarm-body-text .alarm-date {
    width: 30%;
    display: flex;
    align-items: center;
    justify-content: center;
}
.alarm-body-text {
    display: flex;
    height: 60%;
    justify-content: center;
    align-items: center;
}
.alarm-event.selected {
    background: #030303;
}
.alarm-event-icon-message.selected {
    background: #f5f5f5;
}
.handle-alarm-state-div {
    display: flex;
    width: 100%;
    flex-direction: row;
    justify-content: flex-end;
    padding-right: 15px;
    /* margin-bottom: 5px; */
}
.handle-alarm-state-btn {
    margin: 0 2px;
}
.toggle-btn {
    background: #ffffff;
    border: 1px solid #ddd;
    border-radius: 3px;
    padding: 2px 6px;
    font-size: 0.7rem;
    margin-right: 4px;
    cursor: pointer;
    transition: background 0.2s;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
}
.toggle-btn:focus,
.toggle-btn:focus-visible {
    outline: none;
    box-shadow: none;
}

.toggle-btn.active {
    background: #f5f5f5;
}
.alarm-toggle {
    display: flex;
    align-items: center;
    height: 100%;
}
.split-line {
    border-top: 1px solid #ccc;
    /* margin: 10px 0; */
    width: 85%;
    /* margin-top: 15px; */
}
.alarm-severity-font {
    font-size: 10px;
    display: flex;
    justify-content: center;
}
.alarm-bell-icon {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}
.alarm-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;
}
.alarm-type {
    font-weight: bold;
}
.alarm-severity {
    font-size: 0.9em;
    color: #ff5252;
}

.alarm-date {
    display: flex;
    /* justify-content: flex-end; */
    font-size: 0.6rem;
    color: #888;
    margin-bottom: 5px;
}
.alarm-bell {
    width: 50px;
    position: relative;
    margin: 0 15px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
}

.alarm-badge {
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(34%, 20%);
    background-color: red;
    color: #fff;
    border-radius: 12px;
    font-size: 9px;
    display: inline-flex;
    padding: 1px 3px;
    justify-content: center;
    align-items: center;
}
.alarm-event {
    display: flex;
    justify-content: space-around;
    align-items: center;
    cursor: pointer;
    padding: 8px 12px;
    border-bottom: 1px solid #e0e0e0;
    width: 96%;
}
.alarm-event:last-child {
    border: none;
}
.alarm-event-icon-message {
    display: flex;
    align-items: center;
    flex: 1;
}

.alarm-event-text {
    display: flex;
    flex-direction: column;
    font-size: 0.85rem;
    width: 90%;
    margin-left: 10px;
}

.alarm-event-text .alarm-date {
    color: #888;
    font-size: 0.6rem;
    width: 20%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.alarm-event-detail-name {
    width: 80%;
    white-space: normal;
    word-break: break-word;
    font-size: 0.7rem;
    font-weight: bold;
    color: black;
}
.alarm-item {
    display: flex;
    width: 100%;
    max-width: 400px;
    min-width: 250px;
    margin: 4px 0;
}
.alarm-body {
    flex: 1;
    min-width: 0;
    padding: 0 5px;
    /* padding-left: 10px; */
}
.alarm-message {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
    white-space: normal;
    font-size: 0.7rem;
    line-height: 1.3;
    max-height: 2.6em;
    width: 100%;
    height: 100%;
}
.alarm-event-detail-name:empty {
    display: none;
}
.dismiss-alarm {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 10%;
}
.alarm-stop-icon {
    width: 25px;
    height: 25px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #888;
}
.alarm-stop-icon:hover {
    background-color: #e0e0e0;
}
.alarm-list-body {
    display: flex;
    height: 60px;
}
.alarm-event:hover {
    background-color: #f5f5f5;
}
.alarm-event-body {
    max-height: 70vh;
    overflow-y: auto;
    /* padding: 5px 10px; */
}
.alarm-event-hide:hover {
    background: #f5f5f5;
    color: #888;
}
.alarm-event-hide {
    background: none;
    color: #888;
    border: none;
    /* font-size: 1rem; */
    font-weight: bold;
    border-radius: 50%;
    font-size: 1.1rem;
    width: 25px;
    height: 25px;
}
.modal-title {
    white-space: nowrap;
    font-size: 12px;
}

.alarm-event-modal-title {
    width: 80%;
}

.dismiss-alarm.dropdown {
    position: relative;
}

/* .dismiss-alarm.dropdown:hover .dropdown-menu {
    display: block;
} */
.dropdown-item {
    color: white;
}
.dropdown-item:focus {
    outline: none !important;
}
.dismiss-alarm.dropdown .dropdown-menu {
    display: none;
    position: absolute;
    top: 50%;
    right: 0;
    margin-top: 4px;
    z-index: 1000;
}

.dismiss-alarm.dropdown .dropdown-menu.show {
    display: flex;
    flex-direction: row;
    padding: 10px 15px;
    border-radius: 6px;
    /* display: block; */
    box-shadow:
        rgba(17, 17, 26, 0.1) 0px 8px 24px,
        rgba(17, 17, 26, 0.1) 0px 16px 56px,
        rgba(17, 17, 26, 0.1) 0px 24px 80px;
}

.alarm-log-item {
    cursor: pointer;
    transition: background 0.2s;
    border-bottom: 1px solid #e0e0e0;
}
.alarm-log-item--active {
    background-color: #f5f5f5;
}
.alarm-log-item:hover {
    background-color: #f5f5f5;
}
.changeAlarmStateButtonGroup {
    border-radius: 16px;
    display: flex;
}
.alarm-inline-actions {
    display: flex;
    padding: 8px 12px;
    overflow: hidden;
    /* border-top: 1px solid #eee; */
    margin-top: 4px;
    justify-content: center;
}
.handleAlarmEventButton {
    box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;
}
.handleAlarmStateButton {
    font-size: 10px;
    width: 30%;
    box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;
}

.custom-alarm-modal {
    position: absolute;
    top: 0;
    left: -405px;
    width: 400px;
    background: white;
    border-radius: 6px;
    box-shadow:
        0 -2px 6px rgba(0, 0, 0, 0.05),
        0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 9999;
}

.custom-alarm-modal .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border-bottom: 1px solid #eee;
    height: 42px;
    background-color: #e9edf3;
}

.custom-alarm-modal .modal-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 10px;
    max-height: 70vh;
    overflow-y: auto;
    position: relative;
    /* filter: blur(15px); */
}

.custom-alarm-modal .alarm-event-hide {
    background: none;
    border: none;
    font-size: 13px;
    font-weight: bold;
    color: #888;
    cursor: pointer;
    display: flex;
    align-items: center;
    border-radius: 100%;
}
.custom-alarm-modal .alarm-event-hide:focus {
    outline: none;
}
.alarm-event-hide:hover {
    background-color: #e0e0e0;
}
.custom-alarm-modal .alarm-event-button-group {
    font-size: 12px;
    padding: 3px 10px;
    border-radius: 50px;
}
.div-alarmLimit {
    display: flex;
    justify-content: center;
    margin: 9px 0;
}

/* 알람상세 팝업 애니메이션 */
/* .fade-popup-enter-active,
.fade-popup-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-popup-enter-from,
.fade-popup-leave-to {
    opacity: 0;
    transform: scale(0.95);
}
.fade-popup-enter-to,
.fade-popup-leave-from {
    opacity: 1;
    transform: scale(1);
} */

.fade-popup-enter-active,
.fade-popup-leave-active {
    transition: opacity 0.2s;
}
.fade-popup-enter,
.fade-popup-leave-to {
    opacity: 0;
}

.alarm-detail-bottom {
    /* height: 30px; */
    /* padding-bottom: 30px; */
    height: 42px;
    width: 100%;
    background-color: #e9edf3;
    border-radius: 0 0 6px 6px;
}
.alarm-more-icon {
    background: transparent;
}
</style>
